import React, { useCallback, useEffect, useRef, useState } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface TicketConfig {
  enableCustomerPortal: boolean;
  portalRequireEmailVerification: boolean;
  portalCustomDomain?: string | null;
  portalBrandColor?: string | null;
  portalLogoUrl?: string | null;
  portalWelcomeText?: string | null;
  emailLogoUrl?: string | null;
  // non-portal fields (read-only here, just preserved on save)
  siteId?: number;
}

function Toggle({
  checked,
  onChange,
  id,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  id: string;
}) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
        checked ? "bg-primary" : "bg-slate-200"
      }`}
    >
      <span className="sr-only">{checked ? "Enabled" : "Disabled"}</span>
      <span
        aria-hidden="true"
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  id,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  id: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-slate-100 last:border-0">
      <div className="flex-1 min-w-0">
        <label htmlFor={id} className="text-sm font-medium text-slate-800 cursor-pointer">
          {label}
        </label>
        <p className="mt-0.5 text-xs text-slate-500 leading-relaxed">{description}</p>
      </div>
      <div className="flex-shrink-0 pt-0.5">
        <Toggle checked={checked} onChange={onChange} id={id} />
      </div>
    </div>
  );
}

function DnsInstructions() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2 rounded-lg border border-slate-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100"
        aria-expanded={open}
      >
        <span>DNS setup instructions</span>
        <svg
          className={`w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>
      {open && (
        <div className="px-4 py-3 bg-white text-xs text-slate-600 space-y-2 border-t border-slate-100">
          <ol className="space-y-2 list-decimal list-inside">
            <li>
              Add a <strong>CNAME record</strong>:{" "}
              <code className="bg-slate-100 px-1.5 py-0.5 rounded font-mono">
                support.yourcompany.com → portal.velaro.com
              </code>
            </li>
            <li>Wait for DNS propagation (5–60 minutes)</li>
            <li>Enter your domain in the field above and save</li>
            <li>
              SSL is automatically provisioned by Velaro — no certificate management required
            </li>
          </ol>
          <p className="text-slate-400 mt-1">
            Leave the field blank to use the default Velaro portal URL.
          </p>
        </div>
      )}
    </div>
  );
}

const DEFAULT_BRAND_COLOR = "#2563eb";

export default function TicketPortalPanel() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [config, setConfig] = useState<TicketConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    try {
      const resp = await api.messaging.get("ticket/configuration");
      if (resp.ok) {
        const data = await resp.json();
        setConfig(data);
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to load portal configuration.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to load portal configuration.");
    } finally {
      setLoading(false);
    }
  }, [api.messaging, toastAlert]);

  useEffect(() => {
    load();
  }, [load]);

  async function save() {
    if (!config) return;
    setSaving(true);
    try {
      const resp = await api.messaging.put("ticket/configuration", {
        enableCustomerPortal: config.enableCustomerPortal,
        portalRequireEmailVerification: config.portalRequireEmailVerification,
        portalCustomDomain: config.portalCustomDomain ?? null,
        portalBrandColor: config.portalBrandColor ?? null,
        portalLogoUrl: config.portalLogoUrl ?? null,
        portalWelcomeText: config.portalWelcomeText ?? null,
        emailLogoUrl: config.emailLogoUrl ?? null,
      });
      if (resp.ok) {
        const updated = await resp.json();
        setConfig(updated);
        toastAlert.displayToast(Severity.Success, "Portal settings saved.");
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to save portal settings.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to save portal settings.");
    } finally {
      setSaving(false);
    }
  }

  function patch(partial: Partial<TicketConfig>) {
    setConfig((prev) => (prev ? { ...prev, ...partial } : prev));
  }

  async function copyEmbedCode() {
    const snippet = `<script src="https://messaging.velaro.com/embed/ticket-portal.js"\n        data-site-token="{sitePortalToken}"></script>`;
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2500);
    } catch {
      toastAlert.displayToast(Severity.Error, "Could not copy to clipboard.");
    }
  }

  function openPreview() {
    const domain = config?.portalCustomDomain?.trim();
    const url = domain
      ? `https://${domain}`
      : "https://portal.velaro.com";
    window.open(url, "_blank", "noopener,noreferrer");
  }

  const brandColor = config?.portalBrandColor || DEFAULT_BRAND_COLOR;

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-sm text-slate-400 animate-pulse">Loading portal settings…</div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="p-6">
        <div className="text-sm text-red-500">Failed to load configuration. Refresh to retry.</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-base font-semibold text-slate-800">Customer Portal</h2>
        <p className="mt-1 text-sm text-slate-500 leading-relaxed">
          Give customers a self-service portal to view ticket status and reply without logging in.
          Customers receive a secure magic link in ticket emails.
        </p>
      </div>

      {/* Enable / require verification toggles */}
      <div className="bg-white rounded-xl border border-slate-200 px-5 divide-y divide-slate-100">
        <ToggleRow
          id="portal-enable"
          label="Enable Customer Portal"
          description="When enabled, customers receive a secure link in ticket emails to view and reply to their tickets."
          checked={config.enableCustomerPortal}
          onChange={(v) => patch({ enableCustomerPortal: v })}
        />
        <ToggleRow
          id="portal-email-verify"
          label="Require Email Verification"
          description="Compliance mode: customers must verify their email before viewing ticket details. Required for HIPAA, SOC2, and PCI customers."
          checked={config.portalRequireEmailVerification}
          onChange={(v) => patch({ portalRequireEmailVerification: v })}
        />
      </div>

      {/* Email Branding */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Email Branding</h3>
          <p className="mt-1 text-xs text-slate-500">
            Customize the logo shown in outbound ticket confirmation emails sent to customers.
          </p>
        </div>

        {/* Email logo URL */}
        <div>
          <label htmlFor="email-logo-url" className="block text-sm font-medium text-slate-700 mb-1">
            Email Logo URL
          </label>
          <input
            type="url"
            id="email-logo-url"
            value={config.emailLogoUrl ?? ""}
            onChange={(e) => patch({ emailLogoUrl: e.target.value })}
            placeholder="https://yourcompany.com/logo.png"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="mt-1 text-xs text-slate-400">
            Shown at the top of ticket confirmation emails. Use a publicly accessible URL (PNG or SVG recommended, min 200px wide).
            Leave blank to use the Velaro logo.
          </p>
          {config.emailLogoUrl && (
            <div className="mt-2 p-3 rounded-lg border border-slate-200 bg-slate-50 inline-flex items-center gap-2">
              <img
                src={config.emailLogoUrl}
                alt="Email logo preview"
                className="h-8 object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                  (e.currentTarget.nextElementSibling as HTMLElement).style.display = "block";
                }}
              />
              <span className="text-xs text-red-500 hidden">Could not load image — check the URL.</span>
            </div>
          )}
        </div>
      </div>

      {/* Portal Branding */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Portal Branding</h3>
          <p className="mt-1 text-xs text-slate-500">
            Customize the appearance of the customer-facing ticket portal.
          </p>
        </div>

        {/* Brand color */}
        <div>
          <label htmlFor="portal-brand-color" className="block text-sm font-medium text-slate-700 mb-1">
            Brand Color
          </label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              id="portal-brand-color"
              value={brandColor}
              onChange={(e) => patch({ portalBrandColor: e.target.value })}
              className="h-9 w-16 cursor-pointer rounded border border-slate-300 p-0.5 bg-white"
              aria-label="Choose brand color"
            />
            <div className="flex items-center gap-2">
              <div
                className="w-6 h-6 rounded-full border border-slate-200 flex-shrink-0"
                style={{ backgroundColor: brandColor }}
                aria-hidden="true"
              />
              <span className="text-xs font-mono text-slate-500">{brandColor}</span>
            </div>
            <div
              className="ml-2 px-3 py-1.5 rounded text-white text-xs font-medium"
              style={{ backgroundColor: brandColor }}
              aria-hidden="true"
            >
              Preview button
            </div>
          </div>
          <p className="mt-1.5 text-xs text-slate-400">
            Used for the portal header, buttons, and accent elements.
          </p>
        </div>

        {/* Logo URL */}
        <div>
          <label htmlFor="portal-logo-url" className="block text-sm font-medium text-slate-700 mb-1">
            Logo URL
          </label>
          <input
            type="url"
            id="portal-logo-url"
            value={config.portalLogoUrl ?? ""}
            onChange={(e) => patch({ portalLogoUrl: e.target.value })}
            placeholder="https://yourcompany.com/logo.png"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="mt-1 text-xs text-slate-400">
            Shown in the portal header. Leave blank to show only your brand color bar.
          </p>
          {config.portalLogoUrl && (
            <div
              className="mt-2 p-2 rounded border border-slate-200 inline-flex"
              style={{ backgroundColor: config.portalBrandColor || "#f8fafc" }}
            >
              <img
                src={config.portalLogoUrl}
                alt="Logo preview"
                className="h-8 object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).parentElement!.style.display = "none";
                }}
              />
            </div>
          )}
        </div>

        {/* Welcome text */}
        <div>
          <label htmlFor="portal-welcome-text" className="block text-sm font-medium text-slate-700 mb-1">
            Welcome Text
          </label>
          <textarea
            id="portal-welcome-text"
            rows={3}
            value={config.portalWelcomeText ?? ""}
            onChange={(e) => patch({ portalWelcomeText: e.target.value })}
            placeholder="How can we help you today?"
            maxLength={1000}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          />
          <p className="mt-1 text-xs text-slate-400">
            Shown above the ticket list on the portal home page. Plain text only.{" "}
            <span className={`font-mono ${(config.portalWelcomeText?.length ?? 0) >= 950 ? "text-amber-500" : ""}`}>
              {config.portalWelcomeText?.length ?? 0}/1000
            </span>
          </p>
        </div>
      </div>

      {/* Custom domain */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-slate-700">Custom Domain</h3>
        <div>
          <label htmlFor="portal-custom-domain" className="block text-sm font-medium text-slate-700 mb-1">
            Domain
          </label>
          <input
            type="text"
            id="portal-custom-domain"
            value={config.portalCustomDomain ?? ""}
            onChange={(e) => patch({ portalCustomDomain: e.target.value })}
            placeholder="support.yourcompany.com"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="mt-1 text-xs text-slate-400">
            Point your DNS CNAME record to{" "}
            <code className="bg-slate-100 px-1 rounded font-mono">portal.velaro.com</code> to use
            your own domain. Leave blank to use the default Velaro portal URL.
          </p>
        </div>
        <DnsInstructions />
      </div>

      {/* Embed code */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Embed Widget</h3>
          <p className="mt-1 text-xs text-slate-500">
            Add this snippet to any page on your website to show a floating{" "}
            <strong>My Tickets</strong> button that lets customers track their support requests.
          </p>
        </div>
        <div className="relative rounded-lg bg-slate-900 px-4 py-3 overflow-x-auto">
          <pre className="text-xs font-mono text-slate-200 whitespace-pre leading-relaxed">
            {`<script src="https://messaging.velaro.com/embed/ticket-portal.js"\n        data-site-token="{sitePortalToken}"></script>`}
          </pre>
          <button
            type="button"
            onClick={copyEmbedCode}
            aria-label="Copy embed code"
            className={`absolute top-2 right-2 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
              copied
                ? "bg-green-600 text-white"
                : "bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white"
            }`}
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
        <p className="text-xs text-slate-400">
          Replace <code className="bg-slate-100 px-1 rounded font-mono">{"{sitePortalToken}"}</code>{" "}
          with the token shown in the Velaro admin after enabling the portal.
        </p>
      </div>

      {/* Preview + Save */}
      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-active disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
        <button
          type="button"
          onClick={openPreview}
          className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400"
        >
          Preview Portal ↗
        </button>
      </div>
    </div>
  );
}
